import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ButtonGroup Component
 * Renders a group of buttons for selecting from predefined options
 */
const ButtonGroup = ({
  label,
  name,
  options = [],
  value,
  onChange,
  required = false,
  disabled = false,
  error = null,
  hasError = false,
  helpText = null,
  className = '',
  buttonClassName = '',
  labelClassName = ''
}) => {
  const { theme } = useTheme();

  const handleButtonClick = (optionValue) => {
    if (disabled) return;
    
    const event = {
      target: {
        name,
        value: optionValue
      }
    };
    onChange(event);
  };

  const getButtonClasses = (optionValue) => {
    const isSelected = value === optionValue;
    const baseClasses = `
      px-4 py-2 border font-medium transition-all duration-200 
      focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed
      ${buttonClassName}
    `;

    if (isSelected) {
      return `${baseClasses} ${
        theme === 'dark'
          ? 'bg-teal-600 border-teal-600 text-white'
          : 'bg-teal-600 border-teal-600 text-white'
      }`;
    }

    return `${baseClasses} ${
      theme === 'dark'
        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500'
        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
    }`;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label 
          className={`block text-sm font-medium ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          } ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleButtonClick(option.value)}
            disabled={disabled}
            className={`${getButtonClasses(option.value)} ${
              options.length === 3 ? 'first:rounded-l-lg last:rounded-r-lg' : 'rounded-lg'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      
      {helpText && !error && (
        <p className={`text-xs ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>{helpText}</p>
      )}
      
      {error && (
        <p className="text-sm text-red-400 flex items-center">
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default ButtonGroup;
